import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import { GroupLead } from '../../hooks/useOkrEntities';

const AVATAR_COLORS = [
  '#5f016f', '#1a6b3c', '#0d47a1', '#b34700', '#5d4037', '#00695c', '#6a1b9a',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  compactRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: 2,
  },
  compactName: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textAlign: 'right' as const,
    lineHeight: 1.2,
    maxWidth: 110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
}));

interface PersonRowProps {
  accountable: GroupLead;
  avatarSize?: number;
  /** Compact top-right mode: avatar + name stacked, right-aligned */
  compact?: boolean;
  /** Override styles on the name text (non-compact mode only) */
  nameStyle?: React.CSSProperties;
}

export function PersonRow({ accountable, avatarSize = 28, compact = false, nameStyle }: PersonRowProps) {
  const classes = useStyles();

  const userHref = accountable.entityName
    ? `/catalog/default/user/${accountable.entityName}`
    : undefined;

  const avatar = (
    <Avatar
      src={accountable.picture}
      alt={accountable.displayName}
      style={{
        width: avatarSize,
        height: avatarSize,
        fontSize: avatarSize * 0.4,
        fontWeight: 700,
        backgroundColor: accountable.picture ? undefined : avatarColor(accountable.displayName),
        flexShrink: 0,
      }}
    >
      {!accountable.picture && initials(accountable.displayName)}
    </Avatar>
  );

  if (compact) {
    const content = (
      <Box className={classes.compactRow}>
        {avatar}
        <Typography className={classes.compactName}>
          {accountable.displayName}
        </Typography>
      </Box>
    );
    return userHref
      ? <Link to={userHref} style={{ textDecoration: 'none', color: 'inherit' }}>{content}</Link>
      : content;
  }

  const content = (
    <Box className={classes.row}>
      {avatar}
      <Typography variant="body2" style={{ fontWeight: 500, lineHeight: 1.2, ...nameStyle }}>
        {accountable.displayName}
      </Typography>
    </Box>
  );
  return userHref
    ? <Link to={userHref} style={{ textDecoration: 'none', color: 'inherit' }}>{content}</Link>
    : content;
}
